/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { FetchReportProperties } from '../../interface/reports.interface';
import reportApi from '../../utils/api/reports';

interface ReportFilter {
  startDate: string;
  endDate: string;
  year: string;
}

interface ReportState {
  activePinName: string[];
  allBookmarks: any[];
  reportByType: string;
  accountId: string;
  reportFilter: ReportFilter;
  isReportUpdated: boolean;
}

interface GetBookMarkInterFace {
  entity: string;
  accountCustomId: string;
}

export const fetchBookMarksList = createAsyncThunk<FetchReportProperties, GetBookMarkInterFace>(
  'reports/fetchBookMarksList',
  async (data: GetBookMarkInterFace, { dispatch }) => {
    try {
      const response = await reportApi.getBookMarks(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);
const defaultEndDate = dayjs(); // today
const defaultStartDate = dayjs(); // today
const initialState: ReportState = {
  reportByType: 'station',
  accountId: '',
  allBookmarks: [],
  reportFilter: {
    startDate: defaultStartDate.format('YYYY-MM-DD'),
    endDate: defaultEndDate.format('YYYY-MM-DD'),
    year: '',
  },
  isReportUpdated: false,
  activePinName: [],
};

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    /**
     * Updates the report state based on the provided name and value.
     * If the name is 'reportByType', updates the reportByType field.
     * Otherwise, updates the corresponding field in reportFilter and sets isReportUpdated if applicable.
     * @param {any} state - The state
     * @param {any} action - The action
     */
    setReportsData: (
      state,
      action: PayloadAction<{
        name: keyof ReportFilter | 'reportByType';
        value: string;
      }>,
    ) => {
      const { name, value } = action.payload;

      if (name === 'reportByType') {
        return {
          ...state,
          reportByType: value,
        };
      }
      return {
        ...state,
        reportFilter: {
          ...state.reportFilter,
          [name]: value,
        },
        isReportUpdated: ['startDate', 'endDate', 'year'].includes(name),
      };
    },
    /**
     * Resets the isReportUpdated flag to false.
     * @param {any} state - The state
     */
    resetReportUpdateFlag: (state) => {
      return {
        ...state,
        isReportUpdated: false,
      };
    },
    /**
     * Sets the active pin name to the provided value.
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setActivePin: (state, action: PayloadAction<string>) => {
      const pin = action.payload;
      if (state.activePinName.includes(pin)) {
        return {
          ...state,
          activePinName: state.activePinName.filter((item) => item !== pin),
        };
      }
      return {
        ...state,
        activePinName: [...state.activePinName, pin],
      };
    },
    /**
     * Sets the active pin name to the provided value.
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setAccountId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        accountId: action.payload,
      };
    },

    /**
     * Clears the active pin name, setting it to null.
     * @param {any} state - The state
     * @param {any} action - The action
     */
    clearActivePin: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        activePinName: state.activePinName.filter((pin) => pin !== action.payload),
      };
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookMarksList.pending, () => {})
      .addCase(fetchBookMarksList.fulfilled, (state, action) => {
        state.allBookmarks = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchBookMarksList.rejected, () => {});
  },
});

export const { setReportsData, resetReportUpdateFlag, setActivePin, clearActivePin, setAccountId } =
  reportSlice.actions;
export default reportSlice.reducer;
